import React, { useState } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Img } from 'components';
import styled, { keyframes } from 'styled-components';
import Lottie from 'lottie-react';

import Artboard from "assets/images/Artboard.json";
import BackgroundGradient from "assets/images/BackgroundGradient.json";

const flipAnimation = keyframes`
  0% {
    transform: rotateX(0);
  }
  45% {
    transform: rotateX(90deg);
    opacity: 0;
  }
  55% {
    transform: rotateX(-90deg);
    opacity: 0;
  }
  100% {
    transform: rotateX(0);
    opacity: 1;
  }
`;

export const FlipText = styled.span`
  display: inline-block;
  perspective: 1000px;
  position: relative;
  transition: transform 0.5s ease;
  
  & > span {
    display: inline-block;
    transform-style: preserve-3d;
    animation: ${flipAnimation} 0.8s ease;
  }
`;

const LottieBackground = () => {
  return (
    <div className="absolute top-0 left-0 w-screen z-1">
      <Lottie
        animationData={BackgroundGradient}
        loop
        autoplay
        className="w-screen object-cover"
      />
    </div>
  );
};

export default function HeroSection() {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);

  const menuItems = [
    'AI-Marketing Assistant',
    'AI Child Care',
    'AI Fee Collection',
    'AI Communication System',
    'AI Parental Feedback',
    'AI Support',
    'Startup\'s'
  ];

  const features = [
    'Instant Responsive Website',
    'Digital Marketing',
    'Search Engine Optimization',
    '24/7 virtual Admission counsellor',
    'Multilingual Communication-Inspired by PM Modi',
    'Auto Fee Collection',
    'Parental Insights',
    'AI self-healing support',
    'For Startups'
  ];

React.useEffect(() => {
  let timeoutId;

  const cycleFeature = () => {
    setCurrentFeatureIndex((prev) => (prev + 1) % features.length);
    timeoutId = setTimeout(cycleFeature, 3000); // call again after 3 seconds
  };

  // start the first cycle
  timeoutId = setTimeout(cycleFeature, 3000);

  return () => clearTimeout(timeoutId); // cleanup on unmount
}, [features.length]);


  return (
    <>
      {/* <div className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50"> */}
      <div className="relative bg-gradient-to-br from-blue-50 via-white to-orange-50 z-10 overflow-clip">
        <LottieBackground />
        {/* Top Banner */}
        <div className="bg-[#2C76BB] text-[white] text-center py-2 px-4 text-xs sm:text-[10px]">
          Click Here For A Customised <span className="font-semibold">Webinar</span>
        </div>

        <nav className="bg-white sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between items-center h-16">
              {/* Logo */}
              <div className="flex items-center">
                <Img src="images/LogoNew.svg" alt="image" className='w-40' />
                {/* <span className="font-bold text-gray-900 text-base sm:text-sm">
                  SOOMUCH<span className="text-blue-600">.AI</span>
                </span> */}
              </div>

              {/* Desktop Menu Items */}
              <div className="flex md:hidden items-center gap-1">
                {menuItems.map((item, index) => (
                  <button
                    key={index}
                    className="px-2 py-2 text-xs font-medium text-gray-700 hover:text-blue-600 flex items-center space-x-1 transition-colors"
                    onMouseEnter={() => setOpenMenu(index)}
                    onMouseLeave={() => setOpenMenu(null)}
                  >
                    <span>{item}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                ))}
              </div>

              {/* Mobile Menu Button */}
              <div className="hidden md:block">
                <button
                  className="p-2 text-gray-700"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {mobileMenuOpen ? (
                    <X className="w-6 h-6" />
                  ) : (
                    <Menu className="w-6 h-6" />
                  )}
                </button>
              </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {mobileMenuOpen && (
              <div className="hidden md:block pb-4 border-t border-gray-200">
                {menuItems.map((item, index) => (
                  <button
                    key={index}
                    className="w-full px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-blue-600 flex items-center justify-between transition-colors"
                    onClick={() => setOpenMenu(openMenu === index ? null : index)}
                  >
                    <span>{item}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Hero Section */}
        <main className="max-w-7xl mx-auto px-6 py-6 sm:px-4 sm:py-4">
          <div className="grid grid-cols-2 md:grid-cols-1 gap-12 md:gap-8 items-center">
            {/* Left Content */}
            <div className="space-y-8 md:space-y-6 sm:space-y-4">
              <div className="space-y-4 sm:space-y-3">
                <h1 className="text-4xl md:text-3xl sm:text-2xl font-bold text-[#202020] leading-tight">
                  Your 24/7 AI-Powered <br className="sm:hidden" /> marketing partner for schools
                </h1>
                <div className="flex items-baseline space-x-2 text-xs sm:text-xs text-[#636363] flex-wrap">
                  <span>All in just 10 minutes:</span>
                  <span className="text-lg font-bold text-gray-900 relative">
                    <FlipText key={currentFeatureIndex}>
                      <span>{features[currentFeatureIndex]}</span>
                    </FlipText>
                    <Img src="images/new-landing-page/yellowLine.svg" alt="image" className='w-80' />
                  </span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 sm:gap-3">
                <button className="px-6 sm:px-6 py-3 sm:py-2.5 bg-[#2C76BB] text-[white] font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-lg w-fit text-xs sm:text-[11px]">
                  Start Onboarding
                </button>
                <button className="px-6 sm:px-6 py-3 sm:py-2.5 bg-[#EDD23B] text-gray-900 font-medium rounded-lg hover:bg-yellow-500 transition-colors shadow-lg w-fit text-xs sm:text-[11px]">
                  Experience Live Demo
                </button>
              </div>
            </div>

            {/* Right Content - Placeholder for illustration */}
            <div className="relative">
              {/* <div className="w-full h-96 bg-gradient-to-br from-blue-100 to-orange-100 rounded-3xl sm:rounded-2xl flex items-center justify-center"> */}
              <div className="w-full h-96 rounded-3xl sm:rounded-2xl flex items-center justify-center">
                <Lottie animationData={Artboard} className='h-96'  />
                {/* <div className="text-gray-400 text-center">
                  <svg className="w-32 h-32 md:w-24 md:h-24 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm sm:text-xs">Hero Illustration</p>
                </div> */}
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-6 sm:mt-4 bg-white rounded-2xl sm:rounded-xl border border-gray-400 px-8 md:px-6 sm:px-4 py-6 sm:py-4">
            <div className="text-xs sm:text-[10px] grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-8 md:gap-6 sm:gap-4 items-center">
              {/* Funded by Microsoft */}
              <div className="text-center space-y-2 sm:space-y-1">
                <p className="text-[#636363]">Funded by</p>
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-6 h-6 sm:w-5 sm:h-5 grid grid-cols-2 gap-0.5">
                    <div className="bg-red-500"></div>
                    <div className="bg-green-500"></div>
                    <div className="bg-blue-500"></div>
                    <div className="bg-yellow-500"></div>
                  </div>
                  <span className="font-semibold text-gray-900">Microsoft</span>
                </div>
              </div>

              {/* Mindzit Stats */}
              <div className="text-center border-l md:border-l sm:border-l-0 sm:border-t border-r md:border-r-0 border-gray-400 px-4 md:px-4 sm:px-0 sm:pt-4">
                <p className="font-semibold text-gray-900">Mindzit: 5K+ Topics, 500K+</p>
                <p className="font-semibold text-gray-900">Questions, Unlimited Practice.</p>
              </div>

              {/* Error Free */}
              <div className="text-center border-r md:border-r md:border-l-0 sm:border-r-0 sm:border-t border-gray-400 px-4 md:px-4 sm:px-0 sm:pt-4">
                <p className="font-semibold text-gray-900">Error Free : Runs On Self-</p>
                <p className="font-semibold text-gray-900">Healing Technology.</p>
              </div>

              {/* Collaboration */}
              <div className="text-center space-y-1 md:border-l sm:border-l-0 sm:border-t border-gray-400 md:px-4 sm:px-0 sm:pt-4">
                <p className="text-[#636363]">Collaboration with</p>
                <div className="flex items-center justify-center gap-3 sm:gap-2 flex-wrap">
                  <div className="flex items-center">
                    <Img src="images/new-landing-page/meta.svg" alt="image" className='w-6' />
                    <span className="ml-1 font-semibold text-gray-900">Meta</span>
                  </div>
                  <div className="flex items-center">
                    <Img src="images/new-landing-page/google.svg" alt="image" className='w-6' />
                    <span className="ml-1 font-semibold text-gray-900">Google</span>
                  </div>
                  <Img src="images/new-landing-page/startupIndia.svg" alt="image" className='w-20' />
                </div>
              </div>
            </div>
          </div>
        </main>
        {/* <ChatWidget /> */}
      </div>
    </>
  );
}

// function HomePage() {
//   return (
//     <main className="relative min-h-screen overflow-hidden">
//       {/* background wash */}
//       <div className="pointer-events-none absolute inset-0 -z-10 opacity-90">
//         <div className="hero-radial h-full w-full" />
//       </div>

//       {/* top announcement bar */}
//       <div className="bg-primary text-primary-foreground">
//         <div className="mx-auto max-w-7xl px-4 py-2 text-center text-sm">
//           Click Here For A Customised <span className="font-semibold">Webinar</span>
//         </div>
//       </div>

//       <Navbar />

//       <Hero />

//       <div className="mt-12 md:mt-20">
//         <PartnersRibbon />
//       </div>

//       {/* <ChatWidget /> */}
//     </main>
//   )
// }
